import React, { useEffect, useRef, useState } from "react";
import { mixUrl, takeChannel } from "@/lib/audioPool";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useGuidedTour } from "@/lib/guidedTour";

export const FADE_S = 0.6;
export const PAUSE_S = 0.3;
export const TRANSITION_MS = (FADE_S * 2 + PAUSE_S) * 1000;

function StageNav({ view, total, setView }) {
  const btn = "w-11 h-11 rounded-full border border-[#D4AF37]/40 bg-[#0A0612]/80 backdrop-blur-md flex items-center justify-center text-[#F4D88A] hover:bg-[#D4AF37]/15 hover:scale-105 active:scale-95 transition-[background-color,transform] disabled:opacity-30 disabled:pointer-events-none";
  return (
    <div data-testid="exchange-stage-nav">
      <button type="button" data-testid="exchange-stage-prev" aria-label="Previous beat" className={`fixed left-4 top-1/2 -translate-y-1/2 z-[80] ${btn}`} disabled={view === 0} onClick={() => setView(view - 1)}>
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button type="button" data-testid="exchange-stage-next" aria-label="Next beat" className={`fixed right-4 top-1/2 -translate-y-1/2 z-[80] ${btn}`} disabled={view >= total - 1} onClick={() => setView(view + 1)}>
        <ChevronRight className="w-5 h-5" />
      </button>
      <span data-testid="exchange-stage-counter" className="absolute -top-7 right-2 font-mono text-[10px] tracking-[0.24em] text-[#C9A227] tabular-nums">
        BEAT {String(view + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </span>
    </div>
  );
}

// One beat on screen at a time: fade out → gold-black pause → fade in. Narration waits for the fade (see getLeadInMs).
function useSwooshes(view, outUrl, inUrl, enabled) {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    if (!enabled) return undefined;
    const play = (ch, url) => takeChannel(ch, mixUrl(url, 0.45)).play().catch(() => {});
    play("fx1", outUrl);
    const t = setTimeout(() => play("fx2", inUrl), (FADE_S + PAUSE_S) * 1000);
    return () => clearTimeout(t);
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
}

export default function ExchangeStage({ script, view, setView, renderBeat, swooshOut, swooshIn, sfxOn }) {
  const { isActive, step } = useGuidedTour();
  const stageRef = useRef(null);
  useSwooshes(view, swooshOut, swooshIn, sfxOn);
  useEffect(() => { if (isActive) setView(step); }, [isActive, step, setView]);

  useEffect(() => {
    if (isActive) return undefined;
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target?.tagName)) return;
      if (e.key === "ArrowRight") setView((v) => Math.min(script.length - 1, v + 1));
      else if (e.key === "ArrowLeft") setView((v) => Math.max(0, v - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isActive, script.length, setView]);

  const toTop = () => {
    const top = (stageRef.current?.getBoundingClientRect().top || 0) + window.scrollY - 80;
    window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
  };

  const beat = script[view];
  // Timer-driven swap (AnimatePresence "wait" could stall mid-exit and leave the stage black).
  const [shownId, setShownId] = useState(beat?.id);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (beat?.id === shownId) { const t = setTimeout(() => setVisible(true), 30); return () => clearTimeout(t); }
    setVisible(false);
    const t = setTimeout(() => { setShownId(beat?.id); toTop(); }, FADE_S * 1000);
    return () => clearTimeout(t);
  }, [beat?.id, shownId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div ref={stageRef} data-testid="exchange-stage" data-beat={beat?.id}
      className="relative mt-10 min-h-[calc(100vh-96px)] rounded-3xl bg-[#050308] pb-10">
      <div aria-hidden className="pointer-events-none absolute inset-0 rounded-3xl bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.10),transparent_65%)]" />
      <div key={shownId} data-testid={`exchange-stage-beat-${shownId}`} className="relative"
        style={{ opacity: visible ? 1 : 0, transition: visible ? `opacity ${FADE_S}s ease-out ${PAUSE_S}s` : `opacity ${FADE_S}s ease-in` }}>
        {renderBeat(shownId)}
      </div>
      {!isActive && <StageNav view={view} total={script.length} setView={setView} />}
    </div>
  );
}
